import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertCode } from 'shared/types/checkout';
import {
  ApplyCoupon,
  CouponFormProps,
} from 'components/ui/organisms/coupon-form/types';
import { GEO_DISCOUNT_CODE } from 'constants/cart';
import {
  getIsCTADisabled,
  setAlert,
  setIsCTADisabled,
  useAppDispatch,
  useAppSelector,
} from 'store';
import { calculateDiscountAmount } from 'utils/coupon/calculateDiscountAmount';
import { getGeoDiscountFailureAlert } from 'utils/discount/getGeoDiscountFailureAlert';
import { elementEvents, ElementType } from 'utils/eventing';
import { centsToDollarStringFormatted } from 'utils/price';
import { useGeoDiscount } from '../useGeoDiscount';

type UseCouponFormProps = Pick<
  CouponFormProps,
  | 'cartDiscount'
  | 'onApplyCoupon'
  | 'onRemoveCoupon'
  | 'autoPromoDiscountCode'
  | 'geoDiscount'
> & {
  discountAmount?: string;
};

export const useCouponForm = ({
  cartDiscount,
  onApplyCoupon,
  onRemoveCoupon,
  autoPromoDiscountCode,
  geoDiscount,
  discountAmount: discountAmountProp,
}: UseCouponFormProps) => {
  const dispatch = useAppDispatch();
  const isCTADisabled = useAppSelector(getIsCTADisabled);
  const [isCTAPending, setIsCTAPending] = useState(false);
  const [isCouponInputEmpty, setIsCouponInputEmpty] = useState(true);
  const [canShowSuccessPromoAlert, setCanShowSuccessPromoAlert] =
    useState(false);

  const autoPromoData = useRef({
    hasAutoAppliedPromo: false,
    shouldShowAutoPromoMessage: false,
  });

  const { autoApplyDiscount } = useGeoDiscount();

  const hasDiscount = !!cartDiscount?.discount?.code;
  const couponCode = cartDiscount?.discount?.code ?? '';

  const discountAmount = useMemo(
    () =>
      discountAmountProp ??
      calculateDiscountAmount(cartDiscount, centsToDollarStringFormatted),
    [cartDiscount, discountAmountProp],
  );

  const buttonState = isCTAPending
    ? 'pending'
    : isCTADisabled
      ? 'disabled'
      : 'default';
  const buttonCtaState = isCouponInputEmpty ? 'disabled' : buttonState;

  const { elementClicked } = elementEvents({
    webElement: {
      elementType: ElementType.Button,
      name: 'apply-discount-cta',
      text: 'Apply',
      htmlId: 'apply-discount-click',
      location: 'checkout',
    },
  });

  const { elementViewed: autoPromoAppliedViewedEvent } = elementEvents({
    webElement: {
      elementType: ElementType.Entry,
      htmlId: 'promo-auto-applied',
      location: 'checkout',
      name: 'promo-auto-applied',
      text: 'Promo auto-applied',
    },
  });

  const setCTAState = useCallback(
    (value: boolean) => {
      setIsCTAPending(value);
      dispatch(setIsCTADisabled(value));
    },
    [dispatch],
  );

  const handleApplyCoupon = useCallback(
    async ({ coupon, showSuccessMessage = true }: ApplyCoupon) => {
      if (coupon) {
        elementClicked();
        setCTAState(true);

        const geoDiscountCode = geoDiscount?.discountCode.toLowerCase();

        if (geoDiscount && coupon.toLowerCase() === geoDiscountCode) {
          await autoApplyDiscount(geoDiscount);
          setCTAState(false);
          return;
        }

        if (coupon.startsWith(GEO_DISCOUNT_CODE)) {
          dispatch(
            setAlert(
              getGeoDiscountFailureAlert(
                geoDiscount,
                AlertCode.ERROR_APPLYING_GEO_COUPON,
              ),
            ),
          );
          setCTAState(false);
          return;
        }

        await onApplyCoupon(coupon, showSuccessMessage);
        setCTAState(false);
        setCanShowSuccessPromoAlert(true);
      }
    },
    [
      autoApplyDiscount,
      dispatch,
      elementClicked,
      geoDiscount?.discountCode,
      geoDiscount?.discountPercentage,
      geoDiscount?.productKind,
      geoDiscount?.source,
      onApplyCoupon,
      setCTAState,
    ],
  );

  const handleRemoveCoupon = useCallback(async () => {
    if (cartDiscount?.discount) {
      setCTAState(true);
      await onRemoveCoupon();
      setCTAState(false);
      setIsCouponInputEmpty(true);
      if (autoPromoDiscountCode) {
        autoPromoData.current.hasAutoAppliedPromo = true;
      }
    }
  }, [
    autoPromoDiscountCode,
    cartDiscount?.discount,
    onRemoveCoupon,
    setCTAState,
  ]);

  // Auto-promo alert side effect
  if (
    autoPromoData.current.shouldShowAutoPromoMessage &&
    canShowSuccessPromoAlert &&
    !!discountAmount
  ) {
    autoPromoAppliedViewedEvent();
    dispatch(
      setAlert({
        code: AlertCode.AUTO_PROMO_APPLIED,
        value: `${autoPromoDiscountCode ?? ''}/${discountAmount ?? ''}`,
      }),
    );
    autoPromoData.current.shouldShowAutoPromoMessage = false;
  }

  // Auto-apply promo code on mount
  useEffect(() => {
    if (
      autoPromoDiscountCode &&
      !autoPromoData.current.hasAutoAppliedPromo &&
      !hasDiscount
    ) {
      autoPromoData.current.hasAutoAppliedPromo = true;
      autoPromoData.current.shouldShowAutoPromoMessage = true;

      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      handleApplyCoupon({
        coupon: autoPromoDiscountCode,
        showSuccessMessage: false,
      });
    }
  }, [autoPromoDiscountCode, handleApplyCoupon, hasDiscount]);

  return {
    hasDiscount,
    couponCode,
    discountAmount,
    isCTADisabled,
    isCTAPending,
    isCouponInputEmpty,
    setIsCouponInputEmpty,
    buttonState,
    buttonCtaState,
    handleApplyCoupon,
    handleRemoveCoupon,
    autoPromoData,
  };
};
